import { LINEA_NEGOCIO, LineaNegocio } from '@shared/enums/linea-negocio.enum';
import { PERMISOS_MAPEO_CAMPOS } from '@shared/constants/permisos';
import { RUTAS_CONFIRMING } from '@shared/constants/rutas-confirming.constants';
import { PestanaNavegacion } from '@shared/componentes/pestanas-navegacion/pestanas-navegacion.constants';
import { CAMPO_SISTEMA_FACTURA } from '@mapeo-campos/enums/campo-sistema-factura.enum';
import { CAMPO_SISTEMA_PROVEEDOR } from '@mapeo-campos/enums/campo-sistema-proveedor.enum';
import { UNIDAD_DESCUENTO } from '@mapeo-campos/enums/unidad-descuento.enum';
import { TIPO_ARCHIVO_MAPEO, TipoArchivoMapeo } from '@mapeo-campos/enums/tipo-archivo-mapeo.enum';
import { MinimoLineaNegocio } from '@mapeo-campos/models/mapeo-campos.model';

export const LIMITES_MAPEO_CAMPOS = {
  LONGITUD_MAXIMA_NOMBRE: 40,
  LONGITUD_MAXIMA_ROTULO: 100,
  REGISTROS_POR_PAGINA_HISTORIAL: 10,
  TAMANIO_MAXIMO_MB: 1,
  DESPLAZAMIENTO_ARRIBA: -1,
  DESPLAZAMIENTO_ABAJO: 1
} as const;

export const TIPOS_ARCHIVO_MAPEO = '.csv,.xls,.xlsx';

export const CLAVE_TIPO_ARCHIVO_MAPEO = 'tipoArchivoMapeo';

export const BREADCRUMBS_MAPEO_CAMPOS = {
  CONFIRMING: 'Confirming',
  MAPEO_CAMPOS: 'Mapeo de campos',
  FACTURAS: 'Facturas',
  PROVEEDORES: 'Proveedores',
  INFORMACION_BANCARIA: 'Información bancaria',
  CARGA_MASIVA: 'Carga masiva'
} as const;

export interface PestanaMapeo extends PestanaNavegacion {
  id: TipoArchivoMapeo;
}

export const PESTANAS_MAPEO_CAMPOS: readonly PestanaMapeo[] = [
  {
    id: TIPO_ARCHIVO_MAPEO.FACTURAS,
    titulo: BREADCRUMBS_MAPEO_CAMPOS.FACTURAS,
    ruta: `${RUTAS_CONFIRMING.MAPEO_CAMPOS}/${TIPO_ARCHIVO_MAPEO.FACTURAS}`,
    permisos: [PERMISOS_MAPEO_CAMPOS.GESTIONAR_FACTURAS]
  },
  {
    id: TIPO_ARCHIVO_MAPEO.PROVEEDORES,
    titulo: BREADCRUMBS_MAPEO_CAMPOS.PROVEEDORES,
    ruta: `${RUTAS_CONFIRMING.MAPEO_CAMPOS}/${TIPO_ARCHIVO_MAPEO.PROVEEDORES}`,
    permisos: [PERMISOS_MAPEO_CAMPOS.GESTIONAR_PROVEEDORES]
  },
  {
    id: TIPO_ARCHIVO_MAPEO.INFORMACION_BANCARIA,
    titulo: BREADCRUMBS_MAPEO_CAMPOS.INFORMACION_BANCARIA,
    ruta: `${RUTAS_CONFIRMING.MAPEO_CAMPOS}/${TIPO_ARCHIVO_MAPEO.INFORMACION_BANCARIA}`,
    permisos: [PERMISOS_MAPEO_CAMPOS.GESTIONAR_INFORMACION_BANCARIA]
  }
];

export const SIMBOLOS_MAPEO_CAMPOS = {
  ARRASTRAR: '⠿',
  SUBIR: '↑',
  BAJAR: '↓',
  GUARDAR: '✔',
  QUITAR: '✕',
  SIN_VALOR: '—'
} as const;

export const PREFIJO_CAMPO_PERSONALIZADO = 'personalizado:';

export const CAMPO_PERSONALIZADO = CAMPO_SISTEMA_FACTURA.PERSONALIZADO satisfies typeof CAMPO_SISTEMA_PROVEEDOR.PERSONALIZADO;

export const LINEAS_NEGOCIO_MAPEO: ReadonlyArray<{ etiqueta: string; valor: LineaNegocio }> = [
  { etiqueta: 'Financiación', valor: LINEA_NEGOCIO.CON_FINANCIACION },
  { etiqueta: 'Condicionado', valor: LINEA_NEGOCIO.CONDICIONADO },
  { etiqueta: 'Con Descuento', valor: LINEA_NEGOCIO.CON_DESCUENTO }
];

export const MINIMOS_POR_LINEA: Readonly<Partial<Record<LineaNegocio, MinimoLineaNegocio>>> = {
  [LINEA_NEGOCIO.CON_FINANCIACION]: { etiqueta: 'Financiación', campos: 'NIT, Referencia, Fecha Vencimiento y Valor Inicial' },
  [LINEA_NEGOCIO.CONDICIONADO]: {
    etiqueta: 'Condicionado',
    campos: 'NIT, Referencia, Fecha Vencimiento, Valor Futuro y al menos uno de Valor Inicial o Descuento'
  },
  [LINEA_NEGOCIO.CON_DESCUENTO]: {
    etiqueta: 'Con Descuento',
    campos: 'NIT, Referencia, Fecha Vencimiento, Valor Futuro y al menos uno de Valor Inicial o Descuento'
  }
};

export const OPCIONES_UNIDAD_DESCUENTO: ReadonlyArray<{ etiqueta: string; valor: string }> = [
  { etiqueta: 'Valor monetario', valor: UNIDAD_DESCUENTO.VALOR_MONETARIO },
  { etiqueta: 'Porcentaje', valor: UNIDAD_DESCUENTO.PORCENTAJE }
];

export const MENSAJES_MAPEO_CAMPOS = {
  BOTON_HISTORIAL: 'Historial de cambios',
  ARIA_PESTANAS: 'Tipo de archivo',
  LEYENDA_PESTANAS: 'Cada tipo de archivo tiene su propio mapeo y se guarda de forma independiente.',
  ESTADO_CONFIGURADO: '✔ Configurado',
  ESTADO_SIN_CONFIGURAR: 'Sin configurar',
  SIN_CONFIGURACION_VIGENTE: 'Sin configuración guardada',
  AYUDA_ENCABEZADO: 'Configure el mapeo de columnas de su archivo.',
  AYUDA_ARCHIVO: 'El archivo debe incluir una fila con los rótulos de las columnas. Al cargarlo, el sistema detecta las columnas y aplica su mapeo vigente. Puede descargar la',
  AYUDA_PLANTILLA_FIN: 'para realizar la carga más fácil.',
  ENLACE_DESCARGAR: 'Descargar',
  SUBTITULO_CARGA: 'Tamaño máximo: <strong>1MB</strong> por archivo. Formatos: <strong>CSV, Excel (XLS/XLSX)</strong>.',
  BOTON_CAMBIAR: 'Cambiar',
  CARGANDO: 'Cargando la configuración de mapeo…',
  ERROR_CARGA: 'No fue posible cargar la configuración de mapeo.',
  BOTON_REINTENTAR: 'Reintentar',
  DETECTANDO: 'Detectando las columnas del archivo…',
  ETIQUETA_LINEA: 'Línea de negocio',
  ETIQUETA_COLUMNAS_MAPEADAS: 'Columnas mapeadas',
  SIN_ARCHIVO: 'sin archivo cargado',
  COLUMNAS_ASIGNADAS: 'columnas asignadas',
  ETIQUETA_OBLIGATORIOS: 'Obligatorios:',
  NOTA_MINIMOS: 'Al cargar facturas de la línea',
  NOTA_MINIMOS_FIN: 'se exigirán:',
  TITULO_EMPAREJAMIENTO: 'Emparejamiento de columnas',
  AYUDA_EMPAREJAMIENTO: 'Arrastre una columna sobre un campo para mapearla · reordene ambos paneles arrastrando · clic en una columna para editar',
  TITULO_CAMPOS: 'Campos del sistema',
  TITULO_COLUMNAS: 'Columnas del archivo',
  AYUDA_COLUMNAS: 'arrastrar para mapear / reordenar · clic para editar',
  SIN_EMPAREJAR: 'sin emparejar',
  BADGE_OBLIGATORIO: 'obligatorio',
  BADGE_ALTERNATIVO: 'obligatorio · uno de',
  BADGE_OPCIONAL: 'opcional',
  BADGE_PERSONALIZADO: 'pers.',
  INVERTIR_SIGNO: 'Invertir signo',
  UNIDAD: 'Unidad',
  PLACEHOLDER_UNIDAD: 'Unidad',
  CAMPOS_PERSONALIZADOS: 'Campos personalizados',
  BOTON_AGREGAR: '+ Agregar',
  NOTA_PERSONALIZADOS_RESALTADO: 'no participan en los cálculos',
  NOTA_PERSONALIZADOS_FIN: '· ✕ para quitar',
  TODAS_EMPAREJADAS: '✔ Todas las columnas del archivo están emparejadas',
  EJEMPLO: 'ej:',
  EMPAREJAR_CON: 'Emparejar con un campo',
  PLACEHOLDER_ROTULO: 'Rótulo de la columna (ej: NIT_PROVEEDOR)',
  AYUDA_ROTULO: 'El archivo que cargue deberá traer esta columna con este rótulo.',
  BOTON_LIMPIAR: 'Limpiar mapeo',
  BOTON_GUARDAR: 'Guardar configuración',
  TITULO_HISTORIAL: 'Historial de cambios de configuración',
  COLUMNA_FECHA: 'Fecha',
  COLUMNA_USUARIO: 'Usuario',
  COLUMNA_DESCRIPCION: 'Descripción',
  HISTORIAL_VACIO: 'Aún no hay cambios registrados',
  ERROR_HISTORIAL: 'No fue posible cargar el historial de cambios.',
  CAPTION_HISTORIAL: 'Historial de cambios de la configuración de mapeo',
  BOTON_CERRAR: 'Cerrar',
  ROTULO_VACIO: 'Escriba un rótulo de columna válido (no vacío)',
  ROTULO_MUY_LARGO: 'Máximo 100 caracteres',
  ROTULO_REPETIDO: 'Ya existe una columna con ese rótulo en el archivo',
  NOMBRE_PERSONALIZADO_VACIO: 'Escriba un nombre para el campo personalizado',
  NOMBRE_PERSONALIZADO_MUY_LARGO: 'El nombre del campo personalizado admite máximo 40 caracteres',
  UNIDAD_DESCUENTO_REQUERIDA: 'Seleccione la unidad del campo Descuento: Valor monetario o Porcentaje',
  TITULO_ARCHIVO_ERROR: 'Archivo no válido',
  ARIA_ELIMINAR_MAPEO: 'Quitar la columna de este campo',
  ARIA_QUITAR_PERSONALIZADO: 'Quitar campo personalizado',
  ARIA_ARRASTRAR: 'Arrastrar para reordenar',
  ARIA_SUBIR: 'Mover arriba',
  ARIA_BAJAR: 'Mover abajo',
  ARIA_GUARDAR_ROTULO: 'Guardar rótulo',
  ARIA_EDITAR_ROTULO: 'Editar rótulo de columna',
  configuracionVigente: (fecha: string, usuario: string): string => `Configuración vigente · ${fecha} · ${usuario}`,
  contadorPersonalizados: (cantidad: number): string => `${cantidad}`,
  conteoCampos: (campos: number, mapeados: number): string => `${campos} campos · ${mapeados} mapeados`,
  columnasSinMapeo: (cantidad: number): string => `${cantidad} columna(s) del archivo sin mapeo.`,
  AYUDA_SIN_MAPEO: 'Puede continuar: las columnas sin mapeo no participan en el proceso. Ajuste el mapeo arrastrando columnas sobre un campo del sistema.',
  todasEmparejadas: 'Mapeo vigente aplicado al archivo: todas las columnas quedaron emparejadas',
  obligatoriosFaltantes: (campos: string): string => `Debe mapear los campos obligatorios: ${campos}`,
  TOAST_LIMPIADO: 'Mapeo limpiado',
  nombreRepetido: (nombre: string): string => `Ya existe un campo personalizado «${nombre}»`,
  personalizadoAgregado: (nombre: string): string => `Campo personalizado «${nombre}» agregado (no participa en cálculos)`,
  columnaMapeada: (columna: string, campo: string): string => `«${columna}» mapeada a «${campo}»`,
  columnaDesplazada: (columna: string): string => ` · la anterior «${columna}» quedó desasignada`,
  ariaEmparejar: (columna: string): string => `Emparejar la columna ${columna} con un campo del sistema`,
  ariaColumna: (columna: string): string => `Columna ${columna}: editar rótulo`
} as const;
